/**
 * Fixture-shaped payloads using fields already returned by ops/status,
 * workspace list, and ingest history. Names and error strings come from
 * live seats already shown on Data Flow / API Ingest — not invented vendors.
 */

import type { OpsStatus, OpsStatusPipeline, SystemFlow } from '../types';
import type { HistoryTick, WorkspaceApiLike } from './types';

const WEATHER_APP = 'app_qkzzB';
const TREKKER_APP = 'app_g2U0gH20uTC';

function pipeline(partial: OpsStatusPipeline): OpsStatusPipeline {
  return partial;
}

/** Weather seat — four scheduled forecast feeds, 300s, last success minutes ago. */
export function weatherLiveOps(nowIso = '2026-08-25T22:30:00.000Z'): OpsStatus {
  const now = new Date(nowIso).getTime();
  const minutesAgo = (mins: number) => new Date(now - mins * 60_000).toISOString();
  return {
    success: true,
    cached: true,
    generated_at: nowIso,
    api_pipelines: {
      total: 4,
      enabled: 4,
      disabled: 0,
      error_count: 0,
      pipelines: [
        pipeline({
          pipeline_id: 'pipe_open_meteo',
          api_id: 'api_open_meteo',
          api_name: 'Open-Meteo Weather Forecast API',
          endpoint_id: 'get-current-weather',
          status: 'enabled',
          interval_seconds: 300,
          last_success_at: minutesAgo(1),
          consecutive_errors: 0,
          success_count: 288,
          records_24h: 288,
          census_store: 'api_ingest_history',
          last_error: null,
          health: 'healthy',
          app_id: WEATHER_APP,
        }),
        pipeline({
          pipeline_id: 'pipe_afrigis',
          api_id: 'api_afrigis',
          api_name: 'AfriGIS Weather Forecast API',
          endpoint_id: 'get-daily-by-coords',
          status: 'enabled',
          interval_seconds: 300,
          last_success_at: minutesAgo(4),
          consecutive_errors: 0,
          success_count: 285,
          records_24h: 285,
          census_store: 'api_ingest_history',
          last_error: null,
          health: 'healthy',
          app_id: WEATHER_APP,
        }),
        pipeline({
          pipeline_id: 'pipe_met_norway',
          api_id: 'api_met_norway',
          api_name: 'MET Norway Locationforecast 2.0',
          endpoint_id: 'get-compact-forecast',
          status: 'enabled',
          interval_seconds: 300,
          last_success_at: minutesAgo(4),
          consecutive_errors: 0,
          success_count: 286,
          records_24h: 286,
          census_store: 'api_ingest_history',
          last_error: null,
          health: 'healthy',
          app_id: WEATHER_APP,
        }),
        pipeline({
          pipeline_id: 'pipe_google_weather',
          api_id: 'api_google_weather',
          api_name: 'Google Weather API',
          endpoint_id: 'get-current-conditions',
          status: 'enabled',
          interval_seconds: 300,
          last_success_at: minutesAgo(3),
          consecutive_errors: 0,
          success_count: 280,
          records_24h: 280,
          census_store: 'api_ingest_history',
          last_error: null,
          health: 'healthy',
          app_id: WEATHER_APP,
        }),
      ],
    },
    iot_devices: { total: 0, by_status: {}, devices_with_recent_data: 0 },
    credentials: { total_apis: 4, vault_secured: 4, plaintext: 0, expiring_soon: [] },
    data_volume: {
      ingest_history_total: 274000,
      ingest_last_24h: 929,
      census_store: 'api_ingest_history',
    },
  };
}

export const weatherWorkspaceApis: WorkspaceApiLike[] = [
  { api_id: 'api_open_meteo', api_name: 'Open-Meteo Weather Forecast API', status: 'active' },
  { api_id: 'api_afrigis', api_name: 'AfriGIS Weather Forecast API', status: 'active' },
  { api_id: 'api_met_norway', api_name: 'MET Norway Locationforecast 2.0', status: 'active' },
  { api_id: 'api_google_weather', api_name: 'Google Weather API', status: 'active' },
];

export const weatherSystemFlows: SystemFlow[] = [
  {
    id: 'system-api-ingest__pipe_open_meteo',
    name: 'Open-Meteo ingest',
    nodes: [],
    edges: [],
    status: 'active',
    flow_type: 'api_ingest',
    entity: { type: 'pipeline', id: 'pipe_open_meteo', label: 'pipe_open_meteo' },
    metadata: { category: 'api_ingest', pipeline_id: 'pipe_open_meteo' },
  },
];

/** Enabled feed whose last success is older than 2× 300s — quiet, dashed. */
export function weatherQuietOps(nowIso = '2026-08-25T22:30:00.000Z'): OpsStatus {
  const live = weatherLiveOps(nowIso);
  const stale = new Date(new Date(nowIso).getTime() - 40 * 60_000).toISOString();
  return {
    ...live,
    api_pipelines: {
      ...live.api_pipelines,
      pipelines: live.api_pipelines.pipelines.map((pipeline, index) => (
        index === 0
          ? { ...pipeline, last_success_at: stale, health: 'stale' }
          : pipeline
      )),
    },
  };
}

/** Trekker CNH FieldOps ingest — real last_error from the store. */
export function trekkerBrokenOps(nowIso = '2026-08-23T12:25:46.000Z'): OpsStatus {
  return {
    success: true,
    generated_at: nowIso,
    api_pipelines: {
      total: 1,
      enabled: 1,
      disabled: 0,
      error_count: 1,
      pipelines: [
        pipeline({
          pipeline_id: 'pipe_cnh_fieldops',
          api_id: 'api_cnh_fieldops',
          api_name: 'CNH FieldOps / AFS Connect',
          endpoint_id: 'iso-15143-3-telemetry',
          status: 'enabled',
          interval_seconds: 300,
          last_success_at: null,
          last_run_at: nowIso,
          consecutive_errors: 3,
          success_count: 0,
          records_24h: 0,
          census_store: 'api_ingest_history',
          last_error: 'ssrf_dns_failed',
          health: 'error',
          display_status: 'broken',
          app_id: TREKKER_APP,
        }),
      ],
    },
    iot_devices: { total: 0, by_status: {}, devices_with_recent_data: 0 },
    credentials: { total_apis: 1, vault_secured: 1, plaintext: 0, expiring_soon: [] },
    data_volume: {
      ingest_history_total: 12,
      ingest_last_24h: 0,
      census_store: 'api_ingest_history',
    },
  };
}

export const trekkerWorkspaceApis: WorkspaceApiLike[] = [
  {
    api_id: 'api_cnh_fieldops',
    api_name: 'CNH FieldOps / AFS Connect',
    status: 'active',
    runtime: {
      latest_ingest_at: null,
      last_attempt_at: '2026-08-23T12:25:46.000Z',
      last_error: 'ssrf_dns_failed',
      records_24h: 0,
      census_store: 'api_ingest_history',
      lane: 'broken',
      pipeline_count: 1,
      enabled_pipeline_count: 1,
    },
  },
];

/** GEO empty seat — workspace All (0), no pipelines. */
export const emptyOps: OpsStatus = {
  success: true,
  generated_at: '2026-08-25T22:30:00.000Z',
  api_pipelines: { total: 0, enabled: 0, disabled: 0, error_count: 0, pipelines: [] },
  iot_devices: { total: 0, by_status: {}, devices_with_recent_data: 0 },
  credentials: { total_apis: 0, vault_secured: 0, plaintext: 0, expiring_soon: [] },
  data_volume: {
    ingest_history_total: 0,
    ingest_last_24h: 0,
    census_store: 'api_ingest_history',
  },
};

export const emptyWorkspaceApis: WorkspaceApiLike[] = [];
export const emptySystemFlows: SystemFlow[] = [];

export const weatherHistoryTicks: HistoryTick[] = [
  {
    ingest_id: 'ing_open_meteo_1',
    created_at: '2026-08-25T22:29:00.000Z',
    status: 'published',
    endpoint_id: 'get-current-weather',
    processing_time_ms: 412,
  },
  {
    ingest_id: 'ing_open_meteo_0',
    created_at: '2026-08-25T22:24:00.000Z',
    status: 'published',
    endpoint_id: 'get-current-weather',
    processing_time_ms: 388,
  },
];

export const trekkerHistoryTicks: HistoryTick[] = [
  {
    ingest_id: 'ing_cnh_1',
    created_at: '2026-08-23T12:25:46.000Z',
    status: 'failed',
    endpoint_id: 'iso-15143-3-telemetry',
    processing_time_ms: 80,
  },
];
